import { z } from 'zod';
import { EntityMetaSchema, zEnum, zNonEmpty } from './common.js';

export const WEBSITE_TEMPLATES = ['modern', 'bold', 'professional'] as const;
export type WebsiteTemplate = (typeof WEBSITE_TEMPLATES)[number];

export const SECTION_TYPES = ['hero', 'services', 'about', 'reviews', 'serviceArea', 'cta', 'contact'] as const;
export type SectionType = (typeof SECTION_TYPES)[number];

export const WebsiteSectionSchema = z.object({
  id: z.string(),
  type: zEnum(SECTION_TYPES),
  enabled: z.boolean().default(true),
  props: z.record(z.string(), z.unknown()).default({}),
});
export type WebsiteSection = z.infer<typeof WebsiteSectionSchema>;

export const WebsiteBrandSchema = z.object({
  businessName: zNonEmpty.max(120),
  tagline: z.string().max(200).optional(),
  phone: z.string().max(25).optional(),
  email: z.string().max(160).optional(),
  city: z.string().max(120).optional(),
  primaryColor: z.string().regex(/^#[0-9a-fA-F]{6}$/).default('#155ac4'),
  accentColor: z.string().regex(/^#[0-9a-fA-F]{6}$/).default('#d97706'),
});
export type WebsiteBrand = z.infer<typeof WebsiteBrandSchema>;

export const WebsiteSchema = EntityMetaSchema.extend({
  slug: z.string().regex(/^[a-z0-9-]{3,60}$/, 'Slug: lowercase letters, numbers, hyphens'),
  templateId: zEnum(WEBSITE_TEMPLATES).default('modern'),
  status: z.enum(['draft', 'published']).default('draft'),
  brand: WebsiteBrandSchema,
  sections: z.array(WebsiteSectionSchema).default([]),
  seoTitle: z.string().max(70).optional(),
  seoDescription: z.string().max(180).optional(),
  publishedAt: z.string().optional(),
  customDomain: z.string().max(160).optional(),
});
export type Website = z.infer<typeof WebsiteSchema>;

export const UpdateWebsiteSchema = z.object({
  templateId: zEnum(WEBSITE_TEMPLATES).optional(),
  brand: WebsiteBrandSchema.partial().optional(),
  sections: z.array(WebsiteSectionSchema).optional(),
  seoTitle: z.string().max(70).optional(),
  seoDescription: z.string().max(180).optional(),
});
export type UpdateWebsiteInput = z.infer<typeof UpdateWebsiteSchema>;

/** Public lead-capture payload from a Worksy-hosted site form. */
export const WebsiteLeadSchema = z.object({
  name: zNonEmpty.max(120),
  phone: z.string().max(25).optional(),
  email: z.string().max(160).optional(),
  message: z.string().max(2000).optional(),
  website: z.string().max(0).optional(), // honeypot
}).refine((v) => v.phone || v.email, { message: 'Provide a phone or email', path: ['phone'] });
export type WebsiteLeadInput = z.infer<typeof WebsiteLeadSchema>;

/* ---------------------------------------------- default content from tenant data ---------- */

export function defaultSections(businessName: string, industryLabel: string, services: string[]): WebsiteSection[] {
  const svc = (services.length ? services : ['Repairs', 'Installation', 'Maintenance', 'Emergency service'])
    .map((name) => ({ name, description: `Professional ${name.toLowerCase()} you can rely on.` }));
  return [
    { id: 's_hero', type: 'hero', enabled: true, props: { headline: `${industryLabel} you can trust`, sub: `${businessName} — licensed, insured, and on time.`, ctaText: 'Request service' } },
    { id: 's_services', type: 'services', enabled: true, props: { title: 'What we do', items: svc } },
    { id: 's_about', type: 'about', enabled: true, props: { title: `About ${businessName}`, text: `${businessName} is your local ${industryLabel.toLowerCase()} team. We show up on time, do the job right, and stand behind our work.` } },
    { id: 's_reviews', type: 'reviews', enabled: true, props: { title: 'What customers say', items: [{ quote: 'Fast, professional, and fairly priced. Highly recommend!', author: 'A happy customer' }] } },
    { id: 's_area', type: 'serviceArea', enabled: true, props: { title: 'Areas we serve', text: 'Serving the local metro and surrounding communities.' } },
    { id: 's_cta', type: 'cta', enabled: true, props: { text: 'Ready to book? Get in touch today.' } },
    { id: 's_contact', type: 'contact', enabled: true, props: { title: 'Contact us' } },
  ];
}

/* ---------------------------------------------- isomorphic HTML renderer ------------------- */

const esc = (s: unknown): string => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));

function templateCss(t: WebsiteTemplate, brand: WebsiteBrand): string {
  const p = brand.primaryColor;
  const a = brand.accentColor;
  const heroBg = t === 'bold' ? `linear-gradient(135deg, ${p}, ${a})` : t === 'professional' ? '#0f172a' : `linear-gradient(180deg, ${p}12, #fff)`;
  const heroColor = t === 'modern' ? '#0f172a' : '#fff';
  const font = t === 'professional' ? "Georgia, 'Times New Roman', serif" : "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
  const radius = t === 'bold' ? '18px' : t === 'professional' ? '2px' : '12px';
  return `
  :root{--p:${p};--a:${a};--radius:${radius}}
  *{box-sizing:border-box;margin:0} body{font-family:${font};color:#0f172a;line-height:1.6}
  .wrap{max-width:1100px;margin:0 auto;padding:0 20px}
  header{position:sticky;top:0;background:#fff;border-bottom:1px solid #e2e8f0;z-index:10}
  header .wrap{display:flex;align-items:center;justify-content:space-between;height:64px}
  .logo{font-weight:800;font-size:20px;color:var(--p)}
  .navcta{background:var(--p);color:#fff;padding:10px 18px;border-radius:var(--radius);text-decoration:none;font-weight:600}
  .hero{background:${heroBg};color:${heroColor};padding:${t === 'bold' ? '110px' : '90px'} 0}
  .hero h1{font-size:${t === 'bold' ? '52px' : '42px'};line-height:1.1;max-width:16ch}
  .hero p{font-size:20px;margin-top:16px;opacity:.92;max-width:48ch}
  .hero .navcta{display:inline-block;margin-top:28px;background:var(--a)}
  section{padding:64px 0}
  h2{font-size:30px;margin-bottom:8px} .muted{color:#64748b}
  .grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:20px;margin-top:28px}
  .card{border:1px solid #e2e8f0;border-radius:var(--radius);padding:22px;background:#fff}
  .card h3{color:var(--p);margin-bottom:6px}
  .alt{background:#f8fafc}
  .review{border-left:3px solid var(--a);padding:8px 18px;font-style:italic;color:#334155}
  .cta{background:var(--p);color:#fff;text-align:center}
  .cta a{background:#fff;color:var(--p)}
  form{display:grid;gap:12px;max-width:460px;margin-top:20px}
  input,textarea{padding:12px;border:1px solid #cbd5e1;border-radius:var(--radius);font:inherit;width:100%}
  button{background:var(--a);color:#fff;border:0;padding:13px;border-radius:var(--radius);font-weight:700;font-size:16px;cursor:pointer}
  footer{background:#0f172a;color:#cbd5e1;padding:32px 0;text-align:center;font-size:14px}
  .ok{background:#dcfce7;color:#166534;padding:12px;border-radius:var(--radius)}
  @media(max-width:640px){.hero h1{font-size:34px}}
  `;
}

function renderSection(s: WebsiteSection, brand: WebsiteBrand): string {
  const p = s.props as Record<string, any>;
  switch (s.type) {
    case 'hero':
      return `<div class="hero"><div class="wrap"><h1>${esc(p.headline)}</h1><p>${esc(p.sub)}</p><a class="navcta" href="#contact">${esc(p.ctaText ?? 'Contact us')}</a></div></div>`;
    case 'services':
      return `<section><div class="wrap"><h2>${esc(p.title ?? 'Services')}</h2><div class="grid">${(p.items ?? []).map((it: any) => `<div class="card"><h3>${esc(it.name)}</h3><p class="muted">${esc(it.description)}</p></div>`).join('')}</div></div></section>`;
    case 'about':
      return `<section class="alt"><div class="wrap"><h2>${esc(p.title ?? 'About us')}</h2><p class="muted" style="max-width:60ch;margin-top:10px">${esc(p.text)}</p></div></section>`;
    case 'reviews':
      return `<section><div class="wrap"><h2>${esc(p.title ?? 'Reviews')}</h2><div class="grid">${(p.items ?? []).map((r: any) => `<div class="review">“${esc(r.quote)}”<div class="muted" style="margin-top:8px">— ${esc(r.author)}</div></div>`).join('')}</div></div></section>`;
    case 'serviceArea':
      return `<section class="alt"><div class="wrap"><h2>${esc(p.title ?? 'Service area')}</h2><p class="muted">${esc(p.text)}</p></div></section>`;
    case 'cta':
      return `<section class="cta"><div class="wrap"><h2 style="color:#fff">${esc(p.text ?? 'Get in touch')}</h2><a class="navcta" href="#contact" style="display:inline-block;margin-top:18px">Request service</a></div></section>`;
    case 'contact':
      return `<section id="contact"><div class="wrap"><h2>${esc(p.title ?? 'Contact us')}</h2>
        <p class="muted">${brand.phone ? `Call <a href="tel:${esc(brand.phone)}">${esc(brand.phone)}</a> · ` : ''}${brand.email ? esc(brand.email) : ''}</p>
        <form method="post" action="lead" onsubmit="return wsubmit(event)">
          <input name="name" placeholder="Your name" required />
          <input name="phone" placeholder="Phone" />
          <input name="email" type="email" placeholder="Email" />
          <textarea name="message" placeholder="How can we help?" rows="3"></textarea>
          <input name="website" style="display:none" tabindex="-1" autocomplete="off" />
          <button type="submit">Request service</button>
          <div id="wok" class="ok" style="display:none">Thanks! We'll be in touch shortly.</div>
        </form></div></section>`;
    default:
      return '';
  }
}

/** Render a full, self-contained HTML document for a website (isomorphic: API serves it, editor previews it). */
export function renderWebsiteHtml(site: Website): string {
  const b = site.brand;
  const title = site.seoTitle || `${b.businessName} — ${b.tagline || 'Local service you can trust'}`;
  const desc = site.seoDescription || b.tagline || `${b.businessName} — local, licensed, and reliable.`;
  const body = site.sections.filter((s) => s.enabled).map((s) => renderSection(s, b)).join('\n');
  const schema = { '@context': 'https://schema.org', '@type': 'LocalBusiness', name: b.businessName, telephone: b.phone, areaServed: b.city };
  return `<!doctype html><html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">
<title>${esc(title)}</title><meta name="description" content="${esc(desc)}">
<meta property="og:title" content="${esc(title)}"><meta property="og:description" content="${esc(desc)}">
<script type="application/ld+json">${JSON.stringify(schema)}</script>
<style>${templateCss(site.templateId, b)}</style></head><body>
<header><div class="wrap"><span class="logo">${esc(b.businessName)}</span><a class="navcta" href="#contact">${b.phone ? esc(b.phone) : 'Contact'}</a></div></header>
${body}
<footer><div class="wrap">© ${b.businessName}. Powered by Worksy360.</div></footer>
<script>function wsubmit(e){e.preventDefault();var f=e.target;var d={name:f.name.value,phone:f.phone.value,email:f.email.value,message:f.message.value,website:f.website.value};
fetch('lead',{method:'POST',headers:{'content-type':'application/json'},body:JSON.stringify(d)}).then(function(r){if(r.ok){f.reset();document.getElementById('wok').style.display='block';}else{alert('Sorry, something went wrong.');}});return false;}</script>
</body></html>`;
}
